import { Collapse, Button, Empty, Space, Typography } from "antd";
import { observer } from "mobx-react-lite";
import React, { useState } from "react";
import type { VolunteerTask } from "../types";
import type { VolunteerStore } from "../store";
import { SlotCard } from "./SlotCard";
import { SlotFormModal } from "./SlotFormModal";
import { TaskFormModal } from "./TaskFormModal";
import { EmailContactsModal } from "./EmailContactsModal";
import { StackRow, wrapText } from "./responsive";

interface Props {
  store: VolunteerStore;
  task: VolunteerTask;
  isAdmin: boolean;
}

export const TaskSection = observer(({ store, task, isAdmin }: Props) => {
  const [addSlotOpen, setAddSlotOpen] = useState(false);
  const [editTaskOpen, setEditTaskOpen] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);

  const slots = store.slotsForTask(task.objectId!);
  const totalSignups = slots.reduce((sum, slot) => sum + store.signupsForSlot(slot.objectId!).length, 0);
  const totalCapacity = slots.reduce((sum, slot) => sum + (slot.capacity ?? 0), 0);

  const stop = (fn: () => void) => (e: React.MouseEvent) => {
    e.stopPropagation();
    fn();
  };

  return (
    <Collapse
      defaultActiveKey={[]}
      style={{ marginBottom: 16 }}
      items={[
        {
          key: "1",
          forceRender: true,
          label: (
            <StackRow $align="flex-start" $gap={16} style={{ width: "100%" }}>
              <div style={{ flex: 1, minWidth: 300 }}>
                <Typography.Title level={4} style={{ margin: 0, ...wrapText }}>
                  {task.title}
                </Typography.Title>
                {task.description && (
                  <Typography.Paragraph type="secondary" style={{ marginTop: 4, marginBottom: 0, ...wrapText }}>
                    {task.description}
                  </Typography.Paragraph>
                )}
                {task.contactPersonName && (
                  <Typography.Text mark type="secondary" style={{ display: "block", marginTop: 4, ...wrapText }}>
                    Ansprechperson: {task.contactPersonName}
                  </Typography.Text>
                )}
              </div>
              <Space wrap>
                <Typography.Text type="secondary" style={wrapText}>
                  {slots.length} Schichten · {totalSignups}/{totalCapacity || "∞"} besetzt
                </Typography.Text>
                {isAdmin && (
                  <Space wrap>
                    <Button size="small" onClick={stop(() => setEmailOpen(true))}>
                      Alle Kontakte senden
                    </Button>
                    <Button size="small" onClick={stop(() => setEditTaskOpen(true))}>
                      Aufgabe bearbeiten
                    </Button>
                    <Button size="small" type="primary" onClick={stop(() => setAddSlotOpen(true))}>
                      Schicht hinzufügen
                    </Button>
                  </Space>
                )}
              </Space>
            </StackRow>
          ),
          children: (
            <>
              {slots.length === 0 ? (
                <Empty description="Noch keine Schichten" style={{ margin: "16px 0" }} />
              ) : (
                <div>
                  {slots.map((slot) => (
                    <SlotCard key={slot.objectId} store={store} task={task} slot={slot} isAdmin={isAdmin} />
                  ))}
                </div>
              )}

              {isAdmin && (
                <>
                  <SlotFormModal
                    store={store}
                    task={task}
                    open={addSlotOpen}
                    onClose={() => setAddSlotOpen(false)}
                  />
                  <TaskFormModal
                    store={store}
                    task={task}
                    open={editTaskOpen}
                    onClose={() => setEditTaskOpen(false)}
                  />
                  <EmailContactsModal
                    store={store}
                    target={{ taskId: task.objectId }}
                    title={task.title}
                    open={emailOpen}
                    onClose={() => setEmailOpen(false)}
                  />
                </>
              )}
            </>
          ),
        },
      ]}
    />
  );
});
